<template>
	<div v-if="watchlistItem">
		<div>
			<q-toggle v-if="!virtual && !emit" :value="showBundleImages" @input="updateShowBundleImages" :label="$t('label.showBundleImages')"
					  :disable="updating"/>
		</div>
		<div v-if="showBundleViewer">
			<bundle-viewer :log="log"
                     :isVirtual="virtual || props.isVirtual"
                     :height="200"
                     class="q-my-sm"
			/>
		</div>
		<template v-if="allSmallImages.length">
			<div v-if="watchlistItem && watchlistItem.teaser" class="row q-my-md relative-position">
				<p class="text-bold">{{ $t('log.selectedPreviewImage') }}</p>
				<q-img :src="watchlistItem.teaser"
					   class="col-12 col-md-auto log-teaser-image"
					   style="width: 100%;"
					   position="50% 100%"
					   ratio="2.666666666666667"
					   contain
				/>
				<q-btn @click.stop="resetTeaser"
					   class="absolute-top-right teaser-selector"
					   style="top: 10px; right: 30px;"
					   icon="photo"
					   flat dense
				>
					<q-tooltip>{{ $t('tooltip.resetPreviewImage') }}</q-tooltip>
				</q-btn>
			</div>
			<div>
				<q-checkbox :value="allImagesSelected" @input="toggleAll" :label="$t('label.toggleAllLogImages')"/>
			</div>
			<div>
				<veneer-log-images :images="allSmallImages" @select-image="toggleImageExclusion" :large="large"
								   selectable>
					<template v-slot:imageActions="slotProps">
						<q-checkbox :value="checkBoxValue(slotProps.image)"
									@input="toggleImageExclusion(slotProps.imageIndex)"
									class="absolute"
									style="top: 16px; left: 20px;"
									color="primary"
									keep-color
						/>
						<q-btn @click.stop="setTeaser(slotProps.image)"
							   class="absolute-top-right teaser-selector"
							   style="top: 20px; right: 10px;"
							   :class="teaserSelectorClass(slotProps.image)"
							   icon="photo"
							   flat dense
						>
							<q-tooltip>{{ $t('tooltip.asPreviewImage') }}</q-tooltip>
						</q-btn>
					</template>
				</veneer-log-images>
			</div>
		</template>
		<div v-else-if="loading" class="text-center q-my-md">
			<q-spinner-cube color="primary" size="3em"/>
		</div>
	</div>
</template>

<script>
	import VeneerLogImages from "@/components/detail/VeneerLogImages";
	import LogPropertiesResolver from "@/mixins/LogPropertiesResolver";
	import {FETCH_LOG, UPDATE_WATCHLIST_ITEM} from "@/store/actions.type";
	import _isString from 'lodash/isString';
	import LogUtility from "@/mixins/LogUtility";
	import {HIDE_DIALOG, SET_OBJECT_TO_ADD} from "@/store/mutations.type";
	import {mapGetters} from "vuex";
	import BundleViewer from "@/components/detail/BundleViewer";

	export default {
		name: "LogImagesPicker",
		components: {BundleViewer, VeneerLogImages},
		mixins: [LogPropertiesResolver, LogUtility],
		props: {
			props: Object,
			virtualLog: {
				type: Object,
				default: null
			},
			emit: {
				type: Boolean,
				default: false
			},
			// reverses selection process (include instead of exclude)
			reverse: {
				type: Boolean,
				default: false
			},
			large: {
				type: Boolean,
				default: false
			},
			// is image picker for virtual log
			virtual: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				loading: false,
				updating: false,
				showBundleImages: false
			}
		},
		computed: {
			...mapGetters(['watchlist']),
			watchlistItem() {
				return this.props.item;
			},
			log() {
				return this.getConcreteItem(this.props.item);
			},
			allImagesSelected() {
				if (this.reverse) {
					// only require one image to be selected in reverse mode
					return this.watchlistItem.excludedImages.some(image => this.allSmallImages.includes(image));
				} else {
					return this.watchlistItem.excludedImages.length === 0;
				}
			},
			showBundleViewer() {
				return this.props.showBundleViewer || false;
			}
		},
		async created() {
			if (this.watchlistItem && _isString(this.watchlistItem.excludedImages) && this.watchlistItem.excludedImages.length > 1) {
				this.watchlistItem.excludedImages = JSON.parse(this.watchlistItem.excludedImages);
			}
			if (!this.allSmallImages.length && this.emit) {
				this.loading = true;
				const log = await this.$store.dispatch(FETCH_LOG, this.logId);
				this.loading = false;

				this.$store.commit(SET_OBJECT_TO_ADD, log);
			}
			this.showBundleImages = this.watchlistItem.showBundleImages || false;
		},
		watch: {
			watchlistItem(newWatchlistItem) {
				if (_isString(newWatchlistItem.excludedImages) && newWatchlistItem.excludedImages.length > 3) {
					this.watchlistItem.excludedImages = JSON.parse(newWatchlistItem.excludedImages);
				}
			}
		},
		methods: {
			async updateItem() {
				this.updating = true;
				await this.$store.dispatch(UPDATE_WATCHLIST_ITEM, {
					object: this.watchlistItem,
					watchlist: this.watchlist,
					props: {
						teaser: this.watchlistItem.teaser,
						excludedImages: JSON.stringify(this.watchlistItem.excludedImages),
						showBundleImages: this.showBundleImages
					},
					hideSuccess: true
				});
				this.updating = false;
			},
			checkBoxValue(imageUrl) {
				return this.reverse ? this.isExcluded(imageUrl) !== -1 : this.isExcluded(imageUrl) === -1;
			},
			isExcluded(imageUrl) {
				if (this.watchlistItem.excludedImages && this.watchlistItem.excludedImages.findIndex) {
					return this.watchlistItem.excludedImages.findIndex(excludedImage => excludedImage === imageUrl);
				}
				return -1;
			},
			toggleAll() {
				if (this.reverse) {
					if (this.allImagesSelected) {
						this.watchlistItem.excludedImages = this.watchlistItem.excludedImages.filter(image => !this.allSmallImages.includes(image));
					} else {
						this.watchlistItem.excludedImages = [...this.watchlistItem.excludedImages, ...this.allSmallImages];
					}
				} else {
					if (this.allImagesSelected) {
						this.watchlistItem.excludedImages = [...this.allSmallImages];
					} else {
						this.watchlistItem.excludedImages = [];
					}
				}

				if (this.emit) {
					this.$emit('input', this.watchlistItem.excludedImages);
				} else {
					this.updateItem();
				}
			},
			toggleImageExclusion(imageIndex) {
				if (!this.watchlistItem.excludedImages) {
					this.watchlistItem.excludedImages = [];
				}

				const
					imageUrl = this.allSmallImages[imageIndex],
					index = this.isExcluded(imageUrl);

				if (index === -1) {
					this.watchlistItem.excludedImages.push(imageUrl);
				} else {
					this.watchlistItem.excludedImages.splice(index, 1);
				}

				if (this.emit) {
					this.$emit('input', this.watchlistItem.excludedImages);
				} else {
					this.updateItem();
				}
			},
			setTeaser(imageUrl) {
				this.watchlistItem.teaser = imageUrl;

				if (this.emit) {
					this.$emit('change-teaser', imageUrl);
				} else {
					this.updateItem();
				}
			},
			resetTeaser() {
				this.watchlistItem.teaser = '';

				if (this.emit) {
					this.$emit('change-teaser', '');
				} else {
					this.updateItem();
				}
			},
			teaserSelectorClass(imageUrl) {
				if (this.virtualLog) {
					return this.virtualLog.teaser === imageUrl ? 'selected' : '';
				}

				return this.watchlistItem.teaser === imageUrl ? 'selected' : '';
			},
			updateShowBundleImages(value, e) {
				this.showBundleImages = value;
				this.updateItem();
			}
		}
	}
</script>

<style scoped>

</style>